import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import notifications from "../../../../common/Components/Notifications/Notification";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { getCashDay, listLivePrints } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { newRequestId } from "../../rounds/entryQueue";
import { fmtDay } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard from "../CashbookGuard";
import { fmtShortDate, fmtTime, istToday, pageList, refusalText } from "../money";
import { enqueuePhoto, UploadOutcome } from "../photoQueue";
import type { PhotoFileType, PrintView } from "../types";
import Keypad from "../ui/Keypad";
import { grabFrame, isBlurry, preparePhoto, readSheetQr, setTorch, SheetQr, torchSupported } from "./cameraTools";
import "../cashbook.scss";

const QR_READER_ID = "cb-qr-file-reader";

type Kind = "PAY_SHEET" | "SPENDS";

/** The sheet a photo belongs to (from its QR, or picked when the QR could not be read). */
export type Target = { printId: string; sheetNo: string; revision: number; pages: number; rowCount: number | null };

/** Pages of the sheet still without a photo, leaving out the one in hand (1-based, in order). */
export const pagesLeft = (pages: number, done: Iterable<number>, current: number | null): number[] => {
  const have = new Set(done);
  return Array.from({ length: Math.max(0, pages) }, (_, i) => i + 1).filter((p) => p !== current && !have.has(p));
};

/** A photographed print that a reprint replaced: the live revision of the same sheet number, if any. */
export const supersededBy = (qr: Pick<SheetQr, "printId" | "sheetNo" | "revision">, live: PrintView[] | null): PrintView | null => {
  if (!live || live.some((p) => p.printId === qr.printId)) return null;
  return live.find((p) => p.sheetNo === qr.sheetNo && p.revision > qr.revision) || null;
};

type Held = { blob: Blob; url: string; fileType: PhotoFileType; contentType: string; blurScore: number; blurryAccepted: boolean; qr: SheetQr | null };

type Phase =
  | { name: "camera" }
  | { name: "checking" }
  | { name: "blurry" }
  | { name: "pickSheet" }
  | { name: "pickPage" }
  | { name: "already"; at: string | null }
  | { name: "found" }
  | { name: "sending" }
  | { name: "error"; text: string };

const fileTypeOf = (mime: string): { fileType: PhotoFileType; contentType: string } => {
  if (/png/i.test(mime)) return { fileType: "PNG", contentType: "image/png" };
  if (/hei[cf]/i.test(mime)) return { fileType: "HEIC", contentType: "image/heic" };
  return { fileType: "JPG", contentType: "image/jpeg" };
};

/**
 * End-of-day photo (V1_SPEC 11.4 A3-A5, QR not read, already sent, superseded, spends page). The rear
 * camera with a frame guide (or the phone's own camera through a file picker), then: blurry? which sheet
 * and page (from the QR, or picked)? Each page goes up as soon as it is taken (kept on the phone when
 * offline); the supervisor's TOTAL goes with the last page. Sending with pages missing is allowed.
 */
const CameraScreen = () => {
  const { t, lang } = useKT();
  const history = useHistory();
  const { search } = useLocation();
  const status = useCashbookStatus();
  const q = new URLSearchParams(search);
  const kind: Kind = q.get("kind") === "SPENDS" ? "SPENDS" : "PAY_SHEET";
  const askedPrint = q.get("printId");
  const date = /^\d{4}-\d{2}-\d{2}$/.test(q.get("date") || "") ? (q.get("date") as string) : istToday();

  const [phase, setPhase] = useState<Phase>({ name: "camera" });
  const [live, setLive] = useState<PrintView[] | null>(null);
  const [sentAt, setSentAt] = useState<Record<string, string>>({});
  const [held, setHeld] = useState<Held | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [page, setPage] = useState<number | null>(null);
  const [handwritten, setHandwritten] = useState(false);
  /** Pages with a photo: on the server (from the print) or taken now (sent or waiting on the phone). */
  const [done, setDone] = useState<Record<string, number[]>>({});
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [blurryTries, setBlurryTries] = useState(0);
  const [total, setTotal] = useState("");
  const [showTotal, setShowTotal] = useState(false);
  const [queued, setQueued] = useState(0);

  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [camOk, setCamOk] = useState<boolean | null>(null);
  const [torch, setTorchOn] = useState(false);
  const [canTorch, setCanTorch] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Live prints (for "Which sheet is this?", superseded and already-sent checks) and when each was sent.
  useEffect(() => {
    if (kind !== "PAY_SHEET") return;
    let alive = true;
    listLivePrints(14)
      .then((res) => {
        if (!alive) return;
        const prints = res?.success ? (res.data?.prints || []).filter((p) => p.state === "LIVE") : [];
        setLive(prints);
        setDone((d) => {
          const next = { ...d };
          prints.forEach((p) => (next[p.printId] = Array.from(new Set([...(next[p.printId] || []), ...p.pagesSent]))));
          return next;
        });
      })
      .catch(() => alive && setLive([]));
    getCashDay(istToday())
      .then((res) => {
        if (!alive || !res?.success) return;
        const at: Record<string, string> = {};
        (res.data?.sent || []).forEach((s) => s.printId && (at[s.printId] = s.sentAt));
        setSentAt(at);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [kind]);

  // The rear camera while on the camera step; stopped as soon as the photo is taken or the page closes.
  const stopCamera = useCallback(() => {
    stream.current?.getTracks().forEach((tr) => tr.stop());
    stream.current = null;
    setTorchOn(false);
  }, []);

  useEffect(() => {
    if (phase.name !== "camera") return;
    let alive = true;
    const md: any = typeof navigator !== "undefined" ? navigator.mediaDevices : null;
    if (!md?.getUserMedia) {
      setCamOk(false);
      return;
    }
    md.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 2560 }, height: { ideal: 1920 } }, audio: false })
      .then((s: MediaStream) => {
        if (!alive) {
          s.getTracks().forEach((tr) => tr.stop());
          return;
        }
        stream.current = s;
        setCamOk(true);
        setCanTorch(torchSupported(s.getVideoTracks()[0]));
        if (video.current) {
          video.current.srcObject = s;
          video.current.play().catch(() => undefined);
        }
      })
      .catch(() => alive && setCamOk(false));
    return () => {
      alive = false;
      stopCamera();
    };
  }, [phase.name, stopCamera]);

  useEffect(() => () => Object.values(thumbs).forEach((u) => URL.revokeObjectURL(u)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const pagesDone = (printId?: string | null) => (printId ? done[printId] || [] : []);

  /** Sheet and page are known: an already-sent page asks first; otherwise show what was found. */
  const atPage = (tg: Target, p: number) => {
    setTarget(tg);
    setPage(p);
    if (pagesDone(tg.printId).includes(p)) setPhase({ name: "already", at: sentAt[tg.printId] || null });
    else setPhase({ name: "found" });
  };

  const identify = (h: Held) => {
    if (kind === "SPENDS") {
      setPhase({ name: "found" });
      return;
    }
    if (h.qr) {
      const newer = supersededBy(h.qr, live);
      if (newer) {
        setPhase({ name: "error", text: t("This is Print {{old}}, but Print {{new}} exists. Send the photo of Print {{new}}.", { old: h.qr.revision, new: newer.revision }) });
        return;
      }
      const lp = live?.find((p) => p.printId === h.qr!.printId);
      setHandwritten(false);
      atPage({ printId: h.qr.printId, sheetNo: h.qr.sheetNo, revision: h.qr.revision, pages: h.qr.pages, rowCount: lp?.rowCount ?? null }, h.qr.page);
      return;
    }
    // QR not read (handwritten page, folded corner): he says which sheet and page.
    setHandwritten(true);
    const asked = askedPrint ? live?.find((p) => p.printId === askedPrint) : null;
    if (asked) {
      setTarget({ printId: asked.printId, sheetNo: asked.sheetNo, revision: asked.revision, pages: asked.pages, rowCount: asked.rowCount });
      setPhase({ name: "pickPage" });
    } else setPhase({ name: "pickSheet" });
  };

  const process = async (file: Blob) => {
    stopCamera();
    setPhase({ name: "checking" });
    let blob: Blob = file;
    let blurScore = Number.POSITIVE_INFINITY;
    let types = fileTypeOf(file.type || "image/jpeg");
    try {
      const prepared = await preparePhoto(file);
      blob = prepared.blob;
      blurScore = prepared.blurScore;
      types = { fileType: "JPG", contentType: "image/jpeg" };
    } catch {
      // The browser can't decode it (HEIC outside Safari): send the phone's own file as it is.
    }
    const qr = kind === "PAY_SHEET" ? await readSheetQr(blob, QR_READER_ID) : null;
    const h: Held = { blob, url: URL.createObjectURL(blob), ...types, blurScore, blurryAccepted: false, qr };
    setHeld(h);
    if (isBlurry(blurScore)) {
      setBlurryTries((n) => n + 1);
      setPhase({ name: "blurry" });
      return;
    }
    identify(h);
  };

  const shoot = async () => {
    if (!video.current || !video.current.videoWidth) return;
    try {
      process(await grabFrame(video.current));
    } catch {
      setCamOk(false);
    }
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) process(f);
  };

  const retake = () => {
    if (held) URL.revokeObjectURL(held.url);
    setHeld(null);
    setPhase({ name: "camera" });
  };

  const finish = (out: UploadOutcome | null, left: number[]) => {
    status.refresh();
    if (out?.state === "queued" || queued > 0) {
      notifications({ title: t("Saved on phone · {{n}} photos send when the network is back", { n: Math.max(queued, 1) }), message: "", type: "warning" });
    } else if (left.length && target) {
      notifications({ title: t("Pay sheet photo sent ✓ · Pages {{pages}} not sent", { pages: pageList(left) }), message: "", type: "info" });
    } else {
      notifications({ title: t("Photo sent ✓"), message: "", type: "success" });
    }
    history.push(kp.cashBook());
  };

  /** Send the photo in hand; `withTotal` adds the TOTAL he typed. Returns the outcome or null when refused. */
  const send = async (withTotal: boolean): Promise<UploadOutcome | null> => {
    if (!held) return null;
    setPhase({ name: "sending" });
    const out = await enqueuePhoto({
      clientRequestId: newRequestId(),
      blob: held.blob,
      contentType: held.contentType,
      fileType: held.fileType,
      createdAt: Date.now(),
      meta: {
        kind,
        date,
        printId: kind === "PAY_SHEET" ? target?.printId ?? null : null,
        page: kind === "PAY_SHEET" ? page : null,
        qrText: held.qr?.text ?? null,
        handwritten: kind === "PAY_SHEET" && handwritten,
        declaredTotal: withTotal && total ? total : null,
        blurryAccepted: held.blurryAccepted,
        holderUserId: null,
      },
    });
    if (out.state === "refused") {
      if (out.code === "PRINT_SUPERSEDED") {
        setPhase({ name: "error", text: t("This is Print {{old}}, but Print {{new}} exists. Send the photo of Print {{new}}.", { old: target?.revision ?? 1, new: out.data?.liveRevision ?? (target?.revision ?? 1) + 1 }) });
      } else setPhase({ name: "error", text: refusalText(out, t) });
      return null;
    }
    if (out.state === "alreadySent") notifications({ title: t("This photo was already sent at {{time}}.", { time: fmtTime(out.uploadedAt) }), message: "", type: "info" });
    if (out.state === "queued") setQueued((n) => n + 1);
    if (kind === "PAY_SHEET" && target && page) {
      const fromServer = out.state === "sent" ? out.response.print?.pagesSent || [] : [];
      setDone((d) => ({ ...d, [target.printId]: Array.from(new Set([...(d[target.printId] || []), ...fromServer, page])) }));
      setThumbs((m) => ({ ...m, [`${target.printId}:${page}`]: held.url }));
    }
    setHeld(null);
    return out;
  };

  const header = <PageHeader title={kind === "SPENDS" ? t("Send spends page photo") : t("Send pay sheet photo")} back={kp.cashBook()} backLabel={t("Back")} />;
  const reader = <div id={QR_READER_ID} style={{ display: "none" }} aria-hidden="true" />;
  const hiddenInput = <input ref={fileInput} type="file" accept="image/*" capture="environment" onChange={onFile} style={{ display: "none" }} data-testid="cb-file" />;

  if (phase.name === "camera")
    return (
      <div className="cb">
        {header}
        {reader}
        {hiddenInput}
        {camOk !== false ? (
          <>
            <div className="cb-camera">
              <video ref={video} playsInline muted autoPlay aria-label={t("Camera")} />
              <div className="cb-camera__guide" />
              <div className="cb-camera__hint">{camOk === null ? t("Opening the camera…") : t("Keep the whole sheet in the frame, all four corners showing.")}</div>
            </div>
            <div className="cb-shutter-row">
              {canTorch ? (
                <button
                  type="button"
                  className={`cb-chip${torch ? " is-on" : ""}`}
                  aria-pressed={torch}
                  onClick={async () => {
                    const track = stream.current?.getVideoTracks()[0];
                    if (track && (await setTorch(track, !torch))) setTorchOn(!torch);
                  }}
                >
                  {t("Torch")}
                </button>
              ) : (
                <span />
              )}
              <button type="button" className="cb-shutter" aria-label={t("Take photo")} disabled={!camOk} onClick={shoot} />
              <button type="button" className="cb-link" onClick={() => fileInput.current?.click()}>
                {t("Choose from phone")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="cb-card">{t("Keep the whole sheet in the frame, all four corners showing.")}</p>
            <button type="button" className="cb-btn is-primary" onClick={() => fileInput.current?.click()}>
              {t("Take photo")}
            </button>
          </>
        )}
      </div>
    );

  if (phase.name === "checking" || phase.name === "sending")
    return (
      <div className="cb">
        {header}
        {reader}
        <p className="cb-muted" role="status" aria-live="polite">
          {phase.name === "checking" ? t("Checking the photo…") : t("Sending…")}
        </p>
        <div className="cb-skeleton is-big" />
      </div>
    );

  if (phase.name === "error")
    return (
      <div className="cb">
        {header}
        {reader}
        <div className="cb-card is-bad" role="alert">
          <p>{phase.text}</p>
        </div>
        <button type="button" className="cb-btn is-primary" onClick={retake}>
          {t("Retake")}
        </button>
      </div>
    );

  if (phase.name === "blurry")
    return (
      <div className="cb">
        {header}
        {reader}
        {held ? (
          <div className="cb-camera">
            <img src={held.url} alt="" />
          </div>
        ) : null}
        <div className="cb-card is-warn" role="alert">
          <p>{t("Photo is blurry; amounts won't be readable.")}</p>
          {blurryTries >= 2 ? <p>{t("Turn on the torch and take it from above with the sheet on the floor.")}</p> : null}
        </div>
        <button type="button" className="cb-btn is-primary" onClick={retake}>
          {t("Retake")}
        </button>
        <button
          type="button"
          className="cb-btn"
          onClick={() => {
            if (!held) return;
            const h = { ...held, blurryAccepted: true };
            setHeld(h);
            identify(h);
          }}
        >
          {t("Send anyway")}
        </button>
        <p className="cb-muted" style={{ marginTop: 8 }}>
          {t("The owner may not read the amounts and may ask again tomorrow.")}
        </p>
      </div>
    );

  if (phase.name === "pickSheet")
    return (
      <div className="cb">
        {header}
        {reader}
        <h2 className="cb-title">{t("Which sheet is this?")}</h2>
        {live === null ? <div className="cb-skeleton" /> : null}
        {live && !live.length ? <p className="cb-card is-warn">{t("No printed sheets in the last 14 days. Print the pay sheet first.")}</p> : null}
        <ul className="cb-list" style={{ marginTop: 13 }}>
          {(live || []).map((p) => (
            <li key={p.printId}>
              <button
                type="button"
                className="cb-item"
                onClick={() => {
                  setTarget({ printId: p.printId, sheetNo: p.sheetNo, revision: p.revision, pages: p.pages, rowCount: p.rowCount });
                  setPhase({ name: "pickPage" });
                }}
              >
                <span className="cb-item__main">
                  {p.sheetNo} · {t("Print {{n}}", { n: p.revision })}
                  <span className="cb-item__sub">
                    {t("{{n}} people", { n: p.rowCount })} · {fmtDay(p.from)} – {fmtDay(p.to)}
                  </span>
                </span>
                <span aria-hidden="true">›</span>
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className="cb-btn" onClick={retake}>
          {t("Retake")}
        </button>
      </div>
    );

  if (phase.name === "pickPage" && target)
    return (
      <div className="cb">
        {header}
        {reader}
        <h2 className="cb-title">{t("Which page?")}</h2>
        <p className="cb-muted">
          {target.sheetNo} · {t("Print {{n}}", { n: target.revision })}
        </p>
        <div className="cb-chips">
          {Array.from({ length: target.pages }, (_, i) => i + 1).map((p) => (
            <button key={p} type="button" className="cb-chip" onClick={() => atPage(target, p)}>
              {t("Page {{page}}", { page: p })}
              {pagesDone(target.printId).includes(p) ? " ✓" : ""}
            </button>
          ))}
        </div>
      </div>
    );

  if (phase.name === "already" && target && page)
    return (
      <div className="cb">
        {header}
        {reader}
        <div className="cb-card is-warn" role="alertdialog" aria-label={t("Add page")}>
          <p>
            {phase.at
              ? t("Already sent at {{time}}. The new photo is added; the old one stays.", { time: fmtTime(phase.at) })
              : t("This page was already sent. The new photo is added; the old one stays.")}
          </p>
        </div>
        <button type="button" className="cb-btn is-primary" onClick={() => setPhase({ name: "found" })}>
          {t("Add page")}
        </button>
        <button type="button" className="cb-btn" onClick={retake}>
          {t("Don't add")}
        </button>
      </div>
    );

  // Found: what the photo is, the pages still to take, the optional TOTAL, then Next page / Send.
  if (kind === "SPENDS")
    return (
      <div className="cb">
        {header}
        {reader}
        {held ? (
          <div className="cb-thumbs">
            <span className="cb-thumb">
              <img src={held.url} alt="" />
            </span>
          </div>
        ) : null}
        <p className="cb-card is-ok" aria-live="polite">
          {t("Spends page · {{date}}", { date: fmtShortDate(date, lang) })}
        </p>
        <Keypad value={total} onChange={setTotal} label={`${t("Total written on the page")} (${t("optional")})`} id="cb-spends-total" />
        <button
          type="button"
          className="cb-btn is-primary"
          onClick={async () => {
            const out = await send(true);
            if (out) finish(out, []);
          }}
        >
          {t("Send")}
        </button>
        <button type="button" className="cb-link" onClick={retake}>
          {t("Retake")}
        </button>
      </div>
    );

  if (!target || !page) return null;
  const left = pagesLeft(target.pages, pagesDone(target.printId), page);
  const next = left[0];
  const lastOne = left.length === 0;
  return (
    <div className="cb">
      {header}
      {reader}
      {hiddenInput}
      <p className="cb-card is-ok" aria-live="polite">
        {t("Sheet {{sheetNo}} · page {{page}} / {{pages}} found", { sheetNo: target.sheetNo, page, pages: target.pages })}
        {left.length ? ` · ${t("{{n}} pages left", { n: left.length })}` : ""}
      </p>
      <div className="cb-thumbs" aria-label={t("Pages")}>
        {Array.from({ length: target.pages }, (_, i) => i + 1).map((p) => {
          const url = p === page ? held?.url : thumbs[`${target.printId}:${p}`];
          const has = p === page || pagesDone(target.printId).includes(p);
          return (
            <span key={p} className={`cb-thumb${has ? "" : " is-missing"}`}>
              {url ? <img src={url} alt="" /> : has ? "✓" : ""}
              <span className="cb-thumb__n">{p}</span>
            </span>
          );
        })}
      </div>
      {lastOne || showTotal ? (
        <Keypad value={total} onChange={setTotal} label={`${t("Total paid written on the sheet")} (${t("optional")})`} id="cb-sheet-total" />
      ) : (
        <button type="button" className="cb-link" onClick={() => setShowTotal(true)}>
          {t("Total paid written on the sheet")}
        </button>
      )}
      {lastOne ? (
        <button
          type="button"
          className="cb-btn is-primary"
          onClick={async () => {
            const out = await send(true);
            if (out) finish(out, []);
          }}
        >
          {t("Send")}
        </button>
      ) : (
        <>
          <button
            type="button"
            className="cb-btn is-primary"
            onClick={async () => {
              const out = await send(!!total);
              if (out) setPhase({ name: "camera" });
            }}
          >
            {t("Next page ({{page}}/{{pages}})", { page: next, pages: target.pages })}
          </button>
          <button
            type="button"
            className="cb-btn"
            onClick={async () => {
              const out = await send(true);
              if (out) finish(out, left);
            }}
          >
            {t("Send anyway")}
          </button>
          <p className="cb-muted cb-state is-warn" style={{ marginTop: 8 }}>
            {t("Pages {{pages}} will stay unsent", { pages: pageList(left) })}
          </p>
        </>
      )}
      <button type="button" className="cb-link" onClick={retake}>
        {t("Retake")}
      </button>
    </div>
  );
};

const CashCamera = () => (
  <CashbookGuard>
    <CameraScreen />
  </CashbookGuard>
);

export default CashCamera;
